<template>
  <b-container class="referral-jam-sync-connections mb-5">
    <div class="d-flex justify-content-center align-items-center my-5" v-if="state === 'LOADING'">
      <b-spinner type="grow" variant="primary"/>
    </div>
    <div class="d-flex flex-column justify-content-center align-items-center my-5"  v-else-if="state === 'NOT-FOUND'">
      <p>Event not found.</p>
    </div>
    <div class="d-flex flex-column justify-content-center align-items-center my-5"  v-else-if="state === 'NOT-SUPPORTED'">
      <p>This event does not support syncing contacts.</p>
      <b-button :to="{ name: 'EventPage', params: { eventID } }" class="my-5 px-5" variant="primary">Back</b-button>
    </div>
    <template v-else-if="event && event.id">
      <div class="d-flex flex-row justify-content-start align-items-start my-3 border-bottom pb-3">
        <h2 class="m-0">{{ event.title }}</h2>
        <div class="flex-fill"/>
        <b-alert class="m-2 m-md-0" show variant="info" v-if="event.endDate && event.endDate < timeNow">
          <strong>Ended at:</strong> {{ event.endDate | formatDate('Do MMMM YYYY, HH:mm') }}
        </b-alert>
        <b-alert class="m-2 m-md-0" show variant="info" v-else-if="event.startDate && event.endDate && event.startDate < timeNow">
          <strong>Ends in:</strong> {{ eventEndsIn }}
        </b-alert>
        <b-alert class="d-none d-lg-block m-2 m-md-0" show variant="info" v-else-if="event.startDate > timeNow">
          <strong>Starts in:</strong> {{ eventStartsIn }}
        </b-alert>
        <b-dropdown v-if="isRecruiter" no-caret right variant="transparent">
          <template #button-content><font-awesome-icon icon="ellipsis-v"/></template>
          <b-dropdown-item :to="{ name: 'EventPage', params: { eventID: event.id } }">View Page</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'EventRoom', params: { eventID: event.id } }">View Room</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'EventSettings', params: { eventID: event.id } }">Settings</b-dropdown-item>
          <b-dropdown-divider/>
          <b-dropdown-item :to="{ name: 'EventList' }">All Events</b-dropdown-item>
        </b-dropdown>
      </div>

      <template v-if="canSyncContacts">
        <div class="text-center my-3">
          <h3>{{ $t('onboarding.sync-connections.title') }}</h3>
          <h4>{{ $t('onboarding.sync-connections.description') }}</h4>
        </div>

        <b-card class="my-5" body-class="d-flex flex-row justify-content-center align-items-center"
          v-if="event.leaderboard && event.leaderboard.prizes && event.leaderboard.prizes.text">
            <b-img v-if="event.leaderboard.prizes.first && event.leaderboard.prizes.first.image"
              class="img-fit-cover mr-3" height="100" width="100"
              :src="event.leaderboard.prizes.first.image.fileLocation"/>
            <h3>{{ event.leaderboard.prizes.text }}</h3>
        </b-card>

        <contacts-upload-container class="p-0 my-5" v-if="networkPreferences" :preferences="networkPreferences"
          :preSelect="preSelectedUploadProvider" :redirectUriTemplate="redirectUriTemplate"/>

        <contacts-upload-privacy class="my-3"/>
      </template>

      <b-button :to="{ name: 'EventPage', params: { eventID } }" class="my-3 px-5" variant="primary">Back</b-button>
    </template>
  </b-container>
</template>

<script>
import EventPageQuery from '@/graphql/queries/RfjmEventPage.gql';
import RegisterNetworkQuery from '@/graphql/queries/RegisterNetwork.gql';
import RfjmAddParticipantsMutation from '@src/graphql/mutations/RfjmAddParticipants.gql';
import ContactsUploadContainer from '@/components/contacts-upload/Container';
import ContactsUploadPromo from '@/components/contacts-upload/Promo';
import ContactsUploadPrivacy from '@/components/contacts-upload/Privacy';
import eventsMixin from '@src/mixins/events';
import { createVueMetaBranding } from '@src/lib/branding';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    eventID: {
      type: String,
      required: true,
    },
  },
  mixins: [
    eventsMixin,
  ],
  components: {
    ContactsUploadContainer,
    ContactsUploadPromo,
    ContactsUploadPrivacy,
  },
  data: () => ({
    state: null,
  }),
  created() {
    this.state = 'LOADING';
  },
  apollo: {
    event: {
      query: EventPageQuery,
      variables() {
        const eventID = this.eventID.split('-').pop();
        return { eventID };
      },
      update(data) {
        const { company } = data || {};
        const { event } = company || {};
        return event;
      },
      async result({ loading, data }) {
        if (!loading && data && data.company && data.company.event) {
          const { status } = data.company.event;

          if (this.eventID !== this.eventHandle) {
            this.$router.replace({
              ...this.$route,
              params: { ...this.$route.params, eventID: this.eventHandle },
            });
          }

          if (status === 'ACTIVE' && this.canSyncContacts) {
            this.state = 'READY';
          } else if (status === 'ACTIVE') {
            this.state = 'NOT-SUPPORTED';
          } else {
            this.state = 'NOT-FOUND';
          }

          if (this.state === 'READY' && !this.isRecruiterAdmin && !this.eventUserParticipant) {
            await this.addAsEventParticipant();
          }
        } else if (!loading && data && data.company) {
          this.state = 'NOT-FOUND';
        }
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
    networkPreferences: {
      query: RegisterNetworkQuery,
      update({ company }) {
        const { networkPreferences } = company || {};
        return networkPreferences;
      },
      error(err) {
        this.$log.error(err);
      },
    },
  },
  metaInfo() {
    const head = {
      title: null,
      meta: [],
      script: [],
    };

    if (this.event && this.event.id) {
      if (this.event.title) {
        head.title = this.event.title;
      }

      if (this.event.branding) {
        const isAdminUI = process.env.VUE_ENV === 'server' ? false : process.env.ADMIN_UI;
        const nonce = process.env.VUE_ENV === 'server' ? (this.$ssrContext.nonce || '') : '';
        createVueMetaBranding.call(this, head, this.event.branding, { isAdminUI, nonce });
      }
    }

    return head;
  },
  computed: {
    ...mapRoot([ 'user', 'isRecruiter', 'isRecruiterAdmin', 'isEnglishLang' ]),
    redirectUriTemplate() {
      return `${this.$root.origin}${this.$route.path}?provider=$PROVIDER`;
    },
    preSelectedUploadProvider() {
      if (this.$route && this.$route.query && this.$route.query.provider) {
        return this.$route.query.provider;
      } else {
        return null;
      }
    },
  },
  methods: {
    async addAsEventParticipant() {
      try {
        const { id: eventID } = this.event || {};
        this.$assert(eventID, 404, 'Event not found');
        this.$assert(this.user && this.user.id, 401, 'You are not signed-in');

        const { errors } = await this.$apollo.mutate({
          mutation: RfjmAddParticipantsMutation,
          variables: { event: { id: eventID }, users: { self: true } },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('RfjmAddParticipantsMutation', err));
          throw errors.shift();
        }
      } catch (err) {
        this.$notifyErr(err);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/ReferralJam/EventSyncContacts.vue